import React, { useState, useEffect } from 'react';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { ReclamationActionDetailType, ReclamationActionDetailCodes } from 'constants.js';

import GroupeTraitementSelect from 'components/Shared/GroupeTraitementSelect/GroupeTraitementSelect';
import PieceLookup from 'components/Shared/Piece/PieceLookup';
import PieceApercu from 'components/Shared/Piece/PieceApercu';
import { ApiPieceAddAction, ApiPieceGestionLigne } from 'types/apiReturns';

type ActionDetail = {
    code: string;
    type: number;
    libelle: string;
    position: number;
    valeurString?: string;
    valeurInteger?: number;
};

type ActionData = {
    code: string;
    affectationGroupeId: number;
    details: ActionDetail[];
};

type Props = {
    onChange: (actionData: ActionData | null) => void;
    action: { code: string };
    isAdding?: boolean;
};

// Action « Demande de correction Article sur pièce » : réaffectation vers un GT + consignation
// de l'écart d'article. Flux progressif : la pièce concernée doit être validée avant d'afficher
// les champs article/quantité. Layout 2 colonnes (formulaire 40 % / aperçu pièce 60 %).
export default function DemandeCorrectionArticleSurPieceDialogContent({ onChange, action, isAdding = false }: Props) {

    const [groupeId, setGroupeId] = useState<number | null>(null);
    const [piece, setPiece] = useState<ApiPieceAddAction | null>(null);
    const [lignes, setLignes] = useState<ApiPieceGestionLigne[]>([]);
    const [codeArticlePiece, setCodeArticlePiece] = useState('');
    const [quantitePiece, setQuantitePiece] = useState('');
    const [codeArticleRecu, setCodeArticleRecu] = useState('');
    const [quantiteRecue, setQuantiteRecue] = useState('');
    const [commentaire, setCommentaire] = useState('');

    const quantitePieceNum = Number(quantitePiece);
    const quantiteRecueNum = Number(quantiteRecue);

    // Quantité maximale de la ligne choisie : borne la « Quantité article de la pièce concernée ».
    // (La quantité reçue physiquement, elle, est libre : on peut recevoir un tout autre article/volume.)
    // Un même article peut figurer sur plusieurs lignes du BL (reliquats, kits) : le maximum
    // déclarable est le cumul des quantités de toutes les lignes portant ce code article.
    const quantiteLigneMax = lignes
        .filter(l => l.codeArticle === codeArticlePiece)
        .reduce((somme, l) => somme + l.quantite, 0);

    // Recalcule actionData à chaque changement d'état et le remonte au parent.
    // Renvoie null (bouton désactivé) tant qu'un champ obligatoire manque ou est invalide.
    useEffect(() => {

        // Les quantités alimentent des valeurInteger (int? côté serveur) : on exige des entiers
        // pour éviter qu'une saisie décimale passe la validation puis échoue au binding.
        const quantitesValides =
            quantitePiece.trim() !== '' && Number.isInteger(quantitePieceNum) && quantitePieceNum > 0 && quantitePieceNum <= quantiteLigneMax
            && quantiteRecue.trim() !== '' && Number.isInteger(quantiteRecueNum) && quantiteRecueNum > 0;

        if (groupeId === null
            || piece === null
            || codeArticlePiece.trim() === ''
            || codeArticleRecu.trim() === ''
            || !quantitesValides) {
            onChange(null);
            return;
        }

        const details: ActionDetail[] = [
            {
                code: ReclamationActionDetailCodes.NumeroPiece,
                type: ReclamationActionDetailType.LienGeswebBonLivraison,
                libelle: 'Pièce concernée',
                position: 0,
                valeurString: piece.numeroPiece
            },
            {
                code: ReclamationActionDetailCodes.CodeArticleQuantite,
                type: ReclamationActionDetailType.CodeArticleQuantite,
                libelle: 'Article de la pièce concernée',
                position: 1,
                valeurString: codeArticlePiece.trim(),
                valeurInteger: quantitePieceNum
            },
            {
                code: ReclamationActionDetailCodes.CodeArticleQuantiteRecu,
                type: ReclamationActionDetailType.CodeArticleQuantite,
                libelle: 'Article reçu physiquement',
                position: 2,
                valeurString: codeArticleRecu.trim(),
                valeurInteger: quantiteRecueNum
            }
        ];

        if (commentaire.trim() !== '') {
            details.push({
                code: ReclamationActionDetailCodes.Commentaire,
                type: ReclamationActionDetailType.Texte,
                libelle: 'Commentaire',
                position: details.length,
                valeurString: commentaire.trim()
            });
        }

        onChange({
            code: action.code,
            affectationGroupeId: groupeId,
            details
        });

    }, [groupeId, piece, codeArticlePiece, quantitePiece, codeArticleRecu, quantiteRecue, commentaire, quantitePieceNum, quantiteRecueNum, quantiteLigneMax, action.code, onChange]);

    const pieceValidee = piece !== null;
    // Un même article peut figurer sur plusieurs lignes du BL : on dédoublonne par code article
    // pour n'avoir qu'une entrée par article dans le select (le max reste le cumul des lignes).
    const articlesUniques = [...new Map(lignes.map(l => [l.codeArticle, l])).values()];
    // La quantité de la pièce n'est saisissable qu'une fois l'article de la pièce choisi.
    const quantitePieceSaisissable = codeArticlePiece.trim() !== '';
    // La quantité reçue n'est saisissable qu'une fois la quantité de la pièce renseignée (> 0).
    const quantiteRecueSaisissable = quantitePiece.trim() !== '' && quantitePieceNum > 0;

    return (
        <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>

                {/* Colonne gauche : formulaire (40 %) */}
                <Box sx={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', gap: 2 }}>

                    <Typography variant="body2" color="textSecondary">
                        À demander au GT concerné pour traiter le retour du produit reçu physiquement.
                    </Typography>

                    <GroupeTraitementSelect
                        value={groupeId}
                        onChange={setGroupeId}
                        label="Réaffectation au GT"
                        required
                        disabled={isAdding}
                    />

                    <PieceLookup
                        label="Pièce concernée"
                        prefixeLObligatoire
                        disabled={isAdding}
                        onPieceValidee={(p, l) => {
                            setPiece(p);
                            setLignes(l);
                            // La pièce change : on réinitialise les champs dépendants.
                            setCodeArticlePiece('');
                            setQuantitePiece('');
                            setQuantiteRecue('');
                        }}
                    />

                    {pieceValidee && (
                        <>
                            <TextField
                                select
                                label="Code article de la pièce concernée"
                                value={codeArticlePiece}
                                onChange={event => {
                                    setCodeArticlePiece(event.target.value);
                                    // Changer d'article invalide les quantités déjà saisies.
                                    setQuantitePiece('');
                                    setQuantiteRecue('');
                                }}
                                required
                                fullWidth
                                disabled={isAdding}
                            >
                                {articlesUniques.map(ligne => (
                                    <MenuItem key={ligne.codeArticle} value={ligne.codeArticle}>
                                        {ligne.codeArticle} — {ligne.designationArticle}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="Quantité article de la pièce concernée"
                                type="number"
                                value={quantitePiece}
                                onChange={event => {
                                    setQuantitePiece(event.target.value);
                                    setQuantiteRecue('');
                                }}
                                required
                                fullWidth
                                disabled={isAdding || !quantitePieceSaisissable}
                                inputProps={{ min: 1, step: 1, max: quantitePieceSaisissable ? quantiteLigneMax : undefined }}
                                helperText={quantitePieceSaisissable ? `Au maximum ${quantiteLigneMax}` : ''}
                            />

                            <TextField
                                label="Code article reçu physiquement"
                                value={codeArticleRecu}
                                onChange={event => setCodeArticleRecu(event.target.value)}
                                required
                                fullWidth
                                disabled={isAdding}
                            />

                            <TextField
                                label="Quantité reçue physiquement"
                                type="number"
                                value={quantiteRecue}
                                onChange={event => setQuantiteRecue(event.target.value)}
                                required
                                fullWidth
                                disabled={isAdding || !quantiteRecueSaisissable}
                                inputProps={{ min: 1, step: 1 }}
                            />

                            <TextField
                                label="Commentaire"
                                value={commentaire}
                                onChange={event => setCommentaire(event.target.value)}
                                multiline
                                fullWidth
                                disabled={isAdding}
                            />
                        </>
                    )}
                </Box>

                {/* Colonne droite : aperçu de la pièce (60 %) */}
                <Box sx={{ flex: '1 1 60%' }}>
                    <PieceApercu piece={piece} lignes={lignes} />
                </Box>

            </Box>
        </DialogContent>
    );
}
